/**
 * qr-encoder.js — Pure-JS QR code encoder for Cloudflare Workers
 *
 * Zero dependencies. No DOM, canvas, or Node APIs.
 * Supports byte-mode encoding, ECC levels L/M/Q/H, versions 1–10.
 *
 * Usage:
 *   import { encodeQr } from "./qr-encoder.js"
 *   const { size, modules } = encodeQr("https://example.com/p/my-slug")
 *   // size = side length in modules (e.g. 29 for version 2)
 *   // modules = flat boolean[], row-major, true = dark
 */

// ---------------------------------------------------------------------------
// 1. Galois Field GF(256) — primitive polynomial 0x11d (x^8+x^4+x^3+x^2+1)
// ---------------------------------------------------------------------------

const GF_EXP = new Uint8Array(512)
const GF_LOG = new Uint8Array(256)

{
  let x = 1
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x
    GF_LOG[x] = i
    x = (x << 1) ^ (x & 128 ? 0x11d : 0)
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]
}

function gfMul(a, b) {
  if (a === 0 || b === 0) return 0
  return GF_EXP[GF_LOG[a] + GF_LOG[b]]
}

function gfPolyMul(a, b) {
  const r = new Uint8Array(a.length + b.length - 1)
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      r[i + j] ^= gfMul(a[i], b[j])
    }
  }
  return r
}

function rsGeneratorPoly(n) {
  let g = new Uint8Array([1])
  for (let i = 0; i < n; i++) g = gfPolyMul(g, new Uint8Array([1, GF_EXP[i]]))
  return g
}

function rsEncode(data, eccCount) {
  const gen = rsGeneratorPoly(eccCount)
  const msg = new Uint8Array(data.length + eccCount)
  msg.set(data)
  for (let i = 0; i < data.length; i++) {
    const coef = msg[i]
    if (coef !== 0) {
      for (let j = 0; j < gen.length; j++) msg[i + j] ^= gfMul(gen[j], coef)
    }
  }
  return msg.slice(data.length)
}

// ---------------------------------------------------------------------------
// 2. QR data tables (ISO 18004, ECC level M only needed but all provided)
// ---------------------------------------------------------------------------

// Total codewords per version (versions 1–10)
const TOTAL_CODEWORDS = [0, 26, 44, 70, 100, 134, 172, 196, 242, 292, 346]

// ECC block definitions per (ecLevel, version)
// Each entry: [eccCW_per_block, [group1_blocks, group1_dataCW], [group2_blocks, group2_dataCW]?]
const ECC_BLOCKS = {
  L: [
    null,
    [7,  [1, 19]],
    [10, [1, 34]],
    [15, [1, 55]],
    [20, [1, 80]],
    [26, [1, 108]],
    [18, [2, 68]],
    [20, [2, 78]],
    [24, [2, 97]],
    [30, [2, 116]],
    [18, [2, 68], [2, 69]],
  ],
  M: [
    null,
    [10, [1, 16]],
    [16, [1, 28]],
    [26, [1, 44]],
    [18, [2, 32]],
    [24, [2, 43]],
    [16, [4, 27]],
    [18, [4, 31]],
    [22, [2, 38], [2, 39]],
    [22, [3, 36], [2, 37]],
    [26, [4, 43], [1, 44]],
  ],
  Q: [
    null,
    [13, [1, 13]],
    [22, [1, 22]],
    [18, [2, 17]],
    [26, [2, 24]],
    [18, [2, 15], [2, 16]],
    [24, [4, 19]],
    [18, [2, 14], [4, 15]],
    [22, [4, 18], [2, 19]],
    [20, [4, 16], [4, 17]],
    [24, [6, 19], [2, 20]],
  ],
  H: [
    null,
    [17, [1, 9]],
    [28, [1, 16]],
    [22, [2, 13]],
    [16, [4, 9]],
    [22, [2, 11], [2, 12]],
    [28, [4, 15]],
    [26, [4, 13], [1, 14]],
    [26, [4, 14], [2, 15]],
    [24, [4, 12], [4, 13]],
    [28, [6, 15], [2, 16]],
  ]
}

// Alignment pattern center positions per version
const ALIGNMENT_POSITIONS = [
  null,
  [],          // V1: none
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
]

// Character count indicator bit lengths for byte mode
function charCountBits(version) {
  if (version <= 9) return 8
  return 16
}

// ECC level indicators for format info
const EC_LEVEL_BITS = { L: 1, M: 0, Q: 3, H: 2 }

// ---------------------------------------------------------------------------
// 3. Data encoding — byte mode → codeword stream
// ---------------------------------------------------------------------------

function encodeData(text, version, ecLevel) {
  const ecBlocks = ECC_BLOCKS[ecLevel][version]
  const eccPerBlock = ecBlocks[0]
  const totalCW = TOTAL_CODEWORDS[version]

  // Calculate total data codewords
  let dataCW = 0
  for (let g = 1; g < ecBlocks.length; g++) {
    dataCW += ecBlocks[g][0] * ecBlocks[g][1]
  }

  // Encode bit stream
  const bits = []
  function pushBits(value, count) {
    for (let i = count - 1; i >= 0; i--) bits.push((value >> i) & 1)
  }

  // Mode indicator: byte = 0100
  pushBits(0b0100, 4)
  // Character count
  const ccBits = charCountBits(version)
  pushBits(text.length, ccBits)
  // Data bytes (UTF-8 / ISO 8859-1 — for URLs, ASCII is sufficient)
  for (let i = 0; i < text.length; i++) {
    pushBits(text.charCodeAt(i) & 0xFF, 8)
  }
  // Terminator (up to 4 zero bits)
  const totalDataBits = dataCW * 8
  const termLen = Math.min(4, totalDataBits - bits.length)
  for (let i = 0; i < termLen; i++) bits.push(0)
  // Pad to byte boundary
  while (bits.length % 8 !== 0) bits.push(0)
  // Pad codewords (alternating 0xEC, 0x11)
  const padBytes = [0xEC, 0x11]
  let padIdx = 0
  while (bits.length < totalDataBits) {
    pushBits(padBytes[padIdx % 2], 8)
    padIdx++
  }

  // Convert to codeword array
  const dataBytes = new Uint8Array(dataCW)
  for (let i = 0; i < dataCW; i++) {
    let byte = 0
    for (let b = 0; b < 8; b++) byte = (byte << 1) | (bits[i * 8 + b] || 0)
    dataBytes[i] = byte
  }

  // Split into blocks and compute ECC
  const dataBlocks = []
  const eccBlocks = []
  let offset = 0

  for (let g = 1; g < ecBlocks.length; g++) {
    const [numBlocks, blockDataCW] = ecBlocks[g]
    for (let b = 0; b < numBlocks; b++) {
      const blockData = dataBytes.slice(offset, offset + blockDataCW)
      offset += blockDataCW
      dataBlocks.push(blockData)
      eccBlocks.push(rsEncode(blockData, eccPerBlock))
    }
  }

  // Interleave data codewords
  const interleaved = []
  const maxDataLen = Math.max(...dataBlocks.map(b => b.length))
  for (let i = 0; i < maxDataLen; i++) {
    for (const block of dataBlocks) {
      if (i < block.length) interleaved.push(block[i])
    }
  }
  // Interleave ECC codewords
  for (let i = 0; i < eccPerBlock; i++) {
    for (const block of eccBlocks) {
      if (i < block.length) interleaved.push(block[i])
    }
  }

  return interleaved
}

// ---------------------------------------------------------------------------
// 4. Version selection — find smallest version that fits
// ---------------------------------------------------------------------------

function selectVersion(textLength, ecLevel) {
  for (let v = 1; v <= 10; v++) {
    const ecBlocks = ECC_BLOCKS[ecLevel][v]
    let dataCW = 0
    for (let g = 1; g < ecBlocks.length; g++) dataCW += ecBlocks[g][0] * ecBlocks[g][1]
    const ccBits = charCountBits(v)
    const availBits = dataCW * 8
    const neededBits = 4 + ccBits + textLength * 8
    if (neededBits <= availBits) return v
  }
  throw new Error(`QR: text too long (${textLength} bytes) for versions 1-10 at EC level ${ecLevel}`)
}

// ---------------------------------------------------------------------------
// 5. Module placement
// ---------------------------------------------------------------------------

function createMatrix(version) {
  const size = 17 + version * 4
  const modules = new Array(size * size).fill(false)
  const reserved = new Array(size * size).fill(false)
  return { size, modules, reserved }
}

function setModule(matrix, row, col, dark, reserve) {
  const idx = row * matrix.size + col
  matrix.modules[idx] = dark
  if (reserve) matrix.reserved[idx] = true
}

function isReserved(matrix, row, col) {
  return matrix.reserved[row * matrix.size + col]
}

// Finder patterns (3 corners)
function placeFinderPatterns(matrix) {
  const s = matrix.size
  const positions = [[0, 0], [0, s - 7], [s - 7, 0]]

  for (const [startR, startC] of positions) {
    for (let r = 0; r < 7; r++) {
      for (let c = 0; c < 7; c++) {
        const dark = (r === 0 || r === 6 || c === 0 || c === 6) ||
                     (r >= 2 && r <= 4 && c >= 2 && c <= 4)
        setModule(matrix, startR + r, startC + c, dark, true)
      }
    }
  }

  // Separators (1-module white border around finders)
  for (let i = 0; i < 8; i++) {
    // Top-left
    if (i < s) {
      setModule(matrix, 7, i, false, true)
      setModule(matrix, i, 7, false, true)
    }
    // Top-right
    if (s - 8 + i < s) {
      setModule(matrix, 7, s - 8 + i, false, true)
    }
    if (i < 8) {
      setModule(matrix, i, s - 8, false, true)
    }
    // Bottom-left
    if (s - 8 + i < s) {
      setModule(matrix, s - 8 + i, 7, false, true)
    }
    if (i < 8) {
      setModule(matrix, s - 8, i, false, true)
    }
  }
}

// Timing patterns (alternating dark/light)
function placeTimingPatterns(matrix) {
  const s = matrix.size
  for (let i = 8; i < s - 8; i++) {
    const dark = i % 2 === 0
    if (!isReserved(matrix, 6, i)) setModule(matrix, 6, i, dark, true)
    if (!isReserved(matrix, i, 6)) setModule(matrix, i, 6, dark, true)
  }
}

// Alignment patterns
function placeAlignmentPatterns(matrix, version) {
  const positions = ALIGNMENT_POSITIONS[version]
  if (!positions || positions.length === 0) return

  const s = matrix.size
  const centers = []
  for (const r of positions) {
    for (const c of positions) {
      centers.push([r, c])
    }
  }

  for (const [cr, cc] of centers) {
    // Skip if overlapping finder patterns
    if (cr <= 8 && cc <= 8) continue                 // top-left
    if (cr <= 8 && cc >= s - 8) continue             // top-right
    if (cr >= s - 8 && cc <= 8) continue             // bottom-left

    for (let dr = -2; dr <= 2; dr++) {
      for (let dc = -2; dc <= 2; dc++) {
        const dark = Math.abs(dr) === 2 || Math.abs(dc) === 2 || (dr === 0 && dc === 0)
        setModule(matrix, cr + dr, cc + dc, dark, true)
      }
    }
  }
}

// Dark module (always present)
function placeDarkModule(matrix, version) {
  setModule(matrix, 4 * version + 9, 8, true, true)
}

// Reserve format info areas (filled later after masking)
function reserveFormatAreas(matrix) {
  const s = matrix.size
  // Around top-left finder
  for (let i = 0; i < 9; i++) {
    if (!isReserved(matrix, 8, i)) setModule(matrix, 8, i, false, true)
    if (!isReserved(matrix, i, 8)) setModule(matrix, i, 8, false, true)
  }
  // Around top-right finder
  for (let i = 0; i < 8; i++) {
    if (!isReserved(matrix, 8, s - 1 - i)) setModule(matrix, 8, s - 1 - i, false, true)
  }
  // Around bottom-left finder
  for (let i = 0; i < 7; i++) {
    if (!isReserved(matrix, s - 1 - i, 8)) setModule(matrix, s - 1 - i, 8, false, true)
  }
}

// Reserve version info areas (version >= 7 only)
function reserveVersionAreas(matrix, version) {
  if (version < 7) return
  const s = matrix.size
  for (let i = 0; i < 6; i++) {
    for (let j = 0; j < 3; j++) {
      setModule(matrix, i, s - 11 + j, false, true)
      setModule(matrix, s - 11 + j, i, false, true)
    }
  }
}

// Place data bits in zigzag pattern
function placeDataBits(matrix, codewords) {
  const s = matrix.size
  const bits = []
  for (const byte of codewords) {
    for (let b = 7; b >= 0; b--) bits.push((byte >> b) & 1)
  }

  let bitIdx = 0
  let col = s - 1
  let upward = true

  while (col >= 0) {
    // Skip timing pattern column
    if (col === 6) { col--; continue }

    const rows = upward
      ? Array.from({ length: s }, (_, i) => s - 1 - i)
      : Array.from({ length: s }, (_, i) => i)

    for (const row of rows) {
      for (let dc = 0; dc <= 1; dc++) {
        const c = col - dc
        if (c < 0 || c >= s) continue
        if (isReserved(matrix, row, c)) continue
        if (bitIdx < bits.length) {
          matrix.modules[row * s + c] = bits[bitIdx] === 1
          bitIdx++
        }
      }
    }

    col -= 2
    upward = !upward
  }
}

// ---------------------------------------------------------------------------
// 6. Masking
// ---------------------------------------------------------------------------

const MASK_FUNCTIONS = [
  (r, c) => (r + c) % 2 === 0,
  (r, c) => r % 2 === 0,
  (r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
]

function applyMask(matrix, maskIdx) {
  const s = matrix.size
  const fn = MASK_FUNCTIONS[maskIdx]
  for (let r = 0; r < s; r++) {
    for (let c = 0; c < s; c++) {
      if (!matrix.reserved[r * s + c] && fn(r, c)) {
        matrix.modules[r * s + c] = !matrix.modules[r * s + c]
      }
    }
  }
}

// Format info (15 bits: 5 data + 10 ECC, BCH(15,5))
function computeFormatBits(ecLevel, maskIdx) {
  const ecBits = EC_LEVEL_BITS[ecLevel]
  let data = (ecBits << 3) | maskIdx
  let bits = data << 10
  // BCH(15,5) generator: 0b10100110111 = 0x537
  let gen = 0x537
  for (let i = 4; i >= 0; i--) {
    if (bits & (1 << (i + 10))) bits ^= gen << i
  }
  bits = (data << 10) | bits
  // XOR with mask pattern
  bits ^= 0x5412
  return bits
}

function placeFormatBits(matrix, ecLevel, maskIdx) {
  const s = matrix.size
  const bits = computeFormatBits(ecLevel, maskIdx)

  // Bits around top-left finder (reading order)
  const positionsA = [
    [8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5],
    [8, 7], [8, 8],
    [7, 8], [5, 8], [4, 8], [3, 8], [2, 8], [1, 8], [0, 8],
  ]
  for (let i = 0; i < 15; i++) {
    const dark = ((bits >> i) & 1) === 1
    const [r, c] = positionsA[i]
    matrix.modules[r * s + c] = dark
  }

  // Bits around other two finders
  const positionsB = [
    [s - 1, 8], [s - 2, 8], [s - 3, 8], [s - 4, 8], [s - 5, 8], [s - 6, 8], [s - 7, 8],
    [8, s - 8], [8, s - 7], [8, s - 6], [8, s - 5], [8, s - 4], [8, s - 3], [8, s - 2], [8, s - 1],
  ]
  for (let i = 0; i < 15; i++) {
    const dark = ((bits >> i) & 1) === 1
    const [r, c] = positionsB[i]
    matrix.modules[r * s + c] = dark
  }
}

// Penalty scoring for mask evaluation
function computePenalty(matrix) {
  const s = matrix.size
  let penalty = 0

  // Rule 1: Runs of same color (≥5 consecutive)
  for (let r = 0; r < s; r++) {
    let count = 1
    for (let c = 1; c < s; c++) {
      if (matrix.modules[r * s + c] === matrix.modules[r * s + c - 1]) {
        count++
      } else {
        if (count >= 5) penalty += count - 2
        count = 1
      }
    }
    if (count >= 5) penalty += count - 2
  }
  for (let c = 0; c < s; c++) {
    let count = 1
    for (let r = 1; r < s; r++) {
      if (matrix.modules[r * s + c] === matrix.modules[(r - 1) * s + c]) {
        count++
      } else {
        if (count >= 5) penalty += count - 2
        count = 1
      }
    }
    if (count >= 5) penalty += count - 2
  }

  // Rule 2: 2x2 blocks of same color
  for (let r = 0; r < s - 1; r++) {
    for (let c = 0; c < s - 1; c++) {
      const val = matrix.modules[r * s + c]
      if (val === matrix.modules[r * s + c + 1] &&
          val === matrix.modules[(r + 1) * s + c] &&
          val === matrix.modules[(r + 1) * s + c + 1]) {
        penalty += 3
      }
    }
  }

  // Rule 3: Finder-like patterns (1011101 with 4 whites)
  const pattern1 = [true, false, true, true, true, false, true, false, false, false, false]
  const pattern2 = [false, false, false, false, true, false, true, true, true, false, true]
  for (let r = 0; r < s; r++) {
    for (let c = 0; c <= s - 11; c++) {
      let match1 = true, match2 = true
      for (let k = 0; k < 11; k++) {
        const val = matrix.modules[r * s + c + k]
        if (val !== pattern1[k]) match1 = false
        if (val !== pattern2[k]) match2 = false
        if (!match1 && !match2) break
      }
      if (match1 || match2) penalty += 40
    }
  }
  for (let c = 0; c < s; c++) {
    for (let r = 0; r <= s - 11; r++) {
      let match1 = true, match2 = true
      for (let k = 0; k < 11; k++) {
        const val = matrix.modules[(r + k) * s + c]
        if (val !== pattern1[k]) match1 = false
        if (val !== pattern2[k]) match2 = false
        if (!match1 && !match2) break
      }
      if (match1 || match2) penalty += 40
    }
  }

  // Rule 4: Proportion of dark modules
  let darkCount = 0
  for (let i = 0; i < s * s; i++) if (matrix.modules[i]) darkCount++
  const pct = (darkCount * 100) / (s * s)
  const prev5 = Math.floor(pct / 5) * 5
  const next5 = prev5 + 5
  penalty += Math.min(Math.abs(prev5 - 50) / 5, Math.abs(next5 - 50) / 5) * 10

  return penalty
}

// ---------------------------------------------------------------------------
// 7. Public API
// ---------------------------------------------------------------------------

/**
 * Encode text as a QR code.
 * @param {string} text  The text to encode (URL, etc.)
 * @param {string} [ecLevel='M']  Error correction level: 'L', 'M', 'Q', 'H'
 * @returns {{ size: number, modules: boolean[] }}
 *   size: side length in modules (e.g. 25 for version 2)
 *   modules: flat row-major array, true = dark module
 */
export function encodeQr(text, ecLevel = "M") {
  if (!text) throw new Error("QR: text is required")
  if (!ECC_BLOCKS[ecLevel]) throw new Error(`QR: invalid EC level "${ecLevel}"`)

  const version = selectVersion(text.length, ecLevel)
  const codewords = encodeData(text, version, ecLevel)

  // Create matrix and place function patterns
  const matrix = createMatrix(version)
  placeFinderPatterns(matrix)
  placeTimingPatterns(matrix)
  placeAlignmentPatterns(matrix, version)
  placeDarkModule(matrix, version)
  reserveFormatAreas(matrix)
  reserveVersionAreas(matrix, version)

  // Place data
  placeDataBits(matrix, codewords)

  // Evaluate all 8 masks, pick lowest penalty
  let bestMask = 0
  let bestPenalty = Infinity

  // Save unmasked data modules
  const unmasked = matrix.modules.slice()

  for (let m = 0; m < 8; m++) {
    // Restore unmasked state
    for (let i = 0; i < unmasked.length; i++) matrix.modules[i] = unmasked[i]
    applyMask(matrix, m)
    placeFormatBits(matrix, ecLevel, m)
    const p = computePenalty(matrix)
    if (p < bestPenalty) {
      bestPenalty = p
      bestMask = m
    }
  }

  // Apply best mask
  for (let i = 0; i < unmasked.length; i++) matrix.modules[i] = unmasked[i]
  applyMask(matrix, bestMask)
  placeFormatBits(matrix, ecLevel, bestMask)

  return {
    size: matrix.size,
    modules: matrix.modules.map(v => Boolean(v))
  }
}
